import React from 'react';
import CommonButton from '../../../../Components/CommonButton';
import CommonTable from '../../../../Components/CommonTable';
import StatusBadge from '../../../../Components/StatusBadge';
import Section from './Section';
import { Body, heading3, bodySmall } from '../../../../Constants/Fonts';
import { titleCase, fmtDate, fmtDateTime, ratingOf, ratingColor } from './constants';

// RiskReport — read-only audit/report screen shown for a CLOSED risk instead of
// the editable workflow. Surfaces the risk summary, the stage timeline (which
// role owned each stage), the activity/approval log (who did what, when) and the
// comments. No forms, no stage actions.
export default function RiskReport({ detail, stages = [], activity = [], onBack }) {
    const comments = detail.comments || [];
    const ordered = [...stages].sort((a, b) => (a.order || 0) - (b.order || 0));

    // Best-effort actor/role extraction — the activity payload may name the
    // performer under any of these keys depending on the action type.
    const actorOf = (a) => a.performed_by || a.actor_email || a.actor || a.user || a.email || '';
    const roleOf = (a) => a.acting_role || a.actor_role || a.role || '';

    const activityRows = activity.map((a) => ({
        data: [
            <Body as="span" color="var(--color-text-primary)" weight="semibold">{titleCase(a.action || a.event || '—')}</Body>,
            a.detail || a.description || '—',
            <Body as="span" variant={2} color="var(--color-text-secondary)">
                {actorOf(a) || '—'}
                {roleOf(a) && <Body as="span" className="rp1_badge rp1_badge--neutral" style={{ marginLeft: 6 }}>{titleCase(roleOf(a))}</Body>}
            </Body>,
            <Body as="span" variant={2} color="var(--color-text-muted)">{a.performed_at ? fmtDateTime(a.performed_at) : '—'}</Body>,
        ],
    }));

    // Workflow stages → a CommonTable (Stage · Name · Owner · Type · Status).
    // Every stage on a closed risk has been passed through, so all read as
    // "Completed" — the status column keeps the table self-explanatory.
    const stageRows = ordered.map((s, i) => ({
        data: [
            <Body as="span" variant={2} color="var(--color-text-muted)">Stage {i + 1}</Body>,
            <Body as="span" weight="semibold" color="var(--color-text-primary)">{s.name || titleCase(s.id)}</Body>,
            <StatusBadge variant="info" dot={false} label={s.owner_role ? titleCase(s.owner_role) : 'System'} />,
            s.stage_type ? <StatusBadge variant="neutral" dot={false} label={titleCase(s.stage_type)} /> : '—',
            <StatusBadge status="completed" label="Completed" />,
        ],
    }));

    // Risk reduction graphic — inherent vs residual score on the 5×5 (max 25)
    // scale, coloured by rating. Only shown once we have an inherent score.
    const asmt = detail.assessment || {};
    const SCALE_MAX = 25;
    const inherentScore = asmt.inherent_score ?? ((detail.likelihood || 0) * (detail.impact || 0));
    const residualScore = asmt.residual_score;
    const hasResidual = residualScore != null;
    const inherentRating = asmt.inherent_rating || ratingOf(inherentScore);
    const residualRating = hasResidual ? (asmt.residual_rating || ratingOf(residualScore)) : null;
    const reductionPct = inherentScore > 0 && hasResidual
        ? Math.max(0, Math.round(((inherentScore - residualScore) / inherentScore) * 100))
        : null;

    return (
        <div className="rp1_detail">
            {/* Header */}
            <div className="rp1_detail_header">
                <h2 style={heading3({ margin: 0, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', justifyContent: 'space-between' })}>
                    <div>
                        <Body as="span" color="var(--color-primary)" weight="bold">{detail.risk_id} </Body>
                        <Body as="span" color="var(--color-text-primary)">{detail.risk}</Body>
                    </div>
                    <CommonButton text="← Back to Risk List " className="rp1_link_btn" onClick={onBack} />
                </h2>
                <div className="rp1_detail_meta">
                    <StatusBadge status={detail.state} label={detail.state_label || 'Closed'} />
                    {detail.risk_type && <StatusBadge variant="info" dot={false} label={titleCase(detail.risk_type)} />}
                    {detail.risk_owner && <Body as="span" variant={2} color="var(--color-text-secondary)">Owner: {detail.risk_owner}</Body>}
                    {detail.closed_at && <Body as="span" variant={2} color="var(--color-text-muted)">Closed: {fmtDate(detail.closed_at)}</Body>}
                </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', rowGap: 16, marginTop: 16 }}>
                {/* Read-only banner */}
                <div className="rp1_widget" style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--color-surface-muted)' }}>
                    <Body as="span" variant={2} color="var(--color-text-secondary)">
                        This risk is <strong>closed</strong>. Below is a read-only report of its lifecycle — the stages it passed through, the activity log, and its comments.
                    </Body>
                </div>

                {/* Risk summary */}
                <Section title="Risk Summary">
                    <div className="rp1_report_grid">
                        <Field label="Risk ID" value={detail.risk_id} />
                        <Field label="Type" value={detail.risk_type ? titleCase(detail.risk_type) : '—'} />
                        <Field label="Owner" value={detail.risk_owner || '—'} />
                        <Field label="Business Unit" value={detail.business_unit_name || detail.business_unit || '—'} />
                        <Field label="Target Date" value={detail.target_date ? fmtDate(detail.target_date) : '—'} />
                        <Field label="Final State" value={titleCase(detail.state)} />
                    </div>
                    {detail.risk && (
                        <div style={{ marginTop: 12 }}>
                            <Body as="span" variant={2} className="rp1_report_label">Description</Body>
                            <Body variant={2} color="var(--color-text-primary)" style={{ margin: '2px 0 0' }}>{detail.risk}</Body>
                        </div>
                    )}
                </Section>

                {/* Risk reduction — a graphical inherent vs residual comparison,
                    showing how far the risk was reduced across its lifecycle. */}
                {inherentScore > 0 && (
                    <Section title="Risk Reduction">
                        <div className="rp1_bars">
                            <div className="rp1_bar_row">
                                <Body as="span" variant={2} color="var(--color-text-secondary)">Inherent</Body>
                                <span className="rp1_bar_track">
                                    <span className="rp1_bar_fill" style={{ width: `${Math.min(100, (inherentScore / SCALE_MAX) * 100)}%`, background: ratingColor(inherentRating) }} />
                                </span>
                                <Body as="span" variant={2} weight="semibold" className="rp1_bar_value" color="var(--color-text-primary)">
                                    {inherentScore} · {inherentRating}
                                </Body>
                            </div>
                            <div className="rp1_bar_row">
                                <Body as="span" variant={2} color="var(--color-text-secondary)">Residual</Body>
                                <span className="rp1_bar_track">
                                    {hasResidual && <span className="rp1_bar_fill" style={{ width: `${Math.min(100, (residualScore / SCALE_MAX) * 100)}%`, background: ratingColor(residualRating) }} />}
                                </span>
                                <Body as="span" variant={2} weight="semibold" className="rp1_bar_value" color="var(--color-text-primary)">
                                    {hasResidual ? `${residualScore} · ${residualRating}` : 'Not assessed'}
                                </Body>
                            </div>
                        </div>
                        {reductionPct != null && (
                            <Body variant={2} color="var(--color-text-secondary)" style={{ margin: '12px 0 0' }}>
                                Overall risk reduced by <StatusBadge variant="success" dot={false} label={`${reductionPct}%`} /> from inherent to residual.
                            </Body>
                        )}
                    </Section>
                )}

                {/* Stage lifecycle — every stage the risk passed through, with the
                    role that owned it, its type and completion status. */}
                <Section title="Workflow Stages" count={ordered.length}>
                    <CommonTable headers={['Stage', 'Name', 'Owner', 'Type', 'Status']} rows={stageRows} noPadding noScroll />
                </Section>

                {/* Activity / approval log — the authoritative "who did what, when". */}
                <Section title="Activity Log" count={activity.length}>
                    {activityRows.length === 0 ? (
                        <p style={bodySmall({ color: 'var(--color-text-muted)', margin: 0 })}>No activity recorded for this risk.</p>
                    ) : (
                        <CommonTable headers={['Action', 'Details', 'By', 'When']} rows={activityRows} noPadding noScroll />
                    )}
                </Section>

                {/* Comments */}
                <Section title="Comments" count={comments.length}>
                    {comments.length === 0 ? (
                        <p style={bodySmall({ color: 'var(--color-text-muted)', margin: 0 })}>No comments.</p>
                    ) : (
                        <div className="rp1_comment_list">
                            {comments.map((c, i) => (
                                <div key={c.id || i} className="rp1_comment_item">
                                    <Body as="span" variant={2} color="var(--color-text-muted)">{c.author || c.created_by || ''} · {fmtDateTime(c.created_at)}</Body>
                                    <p style={{ margin: '4px 0 0', color: 'var(--color-text-secondary)' }}>{c.text}</p>
                                </div>
                            ))}
                        </div>
                    )}
                </Section>
            </div>
        </div>
    );
}

function Field({ label, value }) {
    return (
        <div className="rp1_report_field">
            <Body as="span" variant={2} className="rp1_report_label">{label}</Body>
            <Body as="span" variant={2} weight="semibold" color="var(--color-text-primary)">{value}</Body>
        </div>
    );
}
